/* eslint-disable no-nested-ternary */
"use client";

import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, type RefObject } from "react";

import type { ModalTheme } from "@/components/types";
import { useRegisterOpenOverlay } from "@/lib/zustand/useRegisterOpenOverlay";
import { useTutorial } from "@/lib/zustand/useTutorial/useTutorial";
import { cn } from "@/utils/cn";

export function Modal({
  open,
  closeModal,
  children,
  title,
  header,
  widthMode,
  minFullHeight,
  initialFocus,
  bgColor = "white",
  centerTitle,
  theme = "light",
  containerClassName,
  panelClassName,
  contentClassName,
  closeButtonClassName,
  closeIconClassName,
}: {
  open: boolean;
  closeModal: (() => void) | null;
  children: React.ReactNode;
  title: string;
  // Takes the title row's place while the dialog keeps its accessible name: for a dialog whose header IS a control (the projects window, whose header is its search field), where a visible <h3> would be a second thing to read above the one you came to use.
  header?: React.ReactNode;
  widthMode: "normal" | "wide" | "ultraWide";
  minFullHeight?: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
  bgColor?: "primaryLight" | "white";
  centerTitle?: boolean;
  // Opt-in dark styling for dialogs living inside dark full-screen tools (TTV): dark panel + light title + darker overlay. Light pages keep the default; when dark, bgColor is ignored.
  theme?: ModalTheme;
  containerClassName?: string;
  panelClassName?: string;
  contentClassName?: string;
  closeButtonClassName?: string;
  closeIconClassName?: string;
}) {
  const isActive = useTutorial((state) => state.isActive);

  // Track this modal in the global open-overlay count while it is shown so document-level keyboard shortcuts can disable themselves behind it — the same registration every rack card that takes the keyboard makes.
  useRegisterOpenOverlay(open);

  // Disable animations when tutorial is active
  const transitionProps =
    isActive ?
      {}
    : {
        enter: "ease-out duration-300",
        enterFrom: "opacity-0",
        enterTo: "opacity-100",
        leave: "ease-in duration-200",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0",
      };

  // Disable child animations when tutorial is active
  const childTransitionProps =
    isActive ?
      {}
    : {
        enter: "ease-out duration-300",
        enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
        enterTo: "opacity-100 translate-y-0 sm:scale-100",
        leave: "ease-in duration-200",
        leaveFrom: "opacity-100 translate-y-0 sm:scale-100",
        leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
      };

  return (
    <Transition.Root show={open}>
      <Dialog
        as="div"
        initialFocus={initialFocus}
        className="relative z-[500]"
        onClose={closeModal === null ? () => {} : closeModal}
      >
        <Transition.Child as={Fragment} {...transitionProps}>
          <div
            // The dark scrim BLURS what it covers rather than only darkening it: a flat 70% black turns the editor into a void and the dialog reads as cut out of the page, where a blur keeps the shape of the thing you are coming back to. A light 4px one: at 12px the editor dissolves into a smear and the blur becomes the loudest thing on screen. The black comes down to 60% to pay for it — at 70 over a blur there is nothing left to see. Light-theme dialogs are unchanged: they cover marketing and account pages, where the thing behind is a document rather than a workspace.
            className={cn(
              theme === "dark" ?
                "bg-black/60 backdrop-blur-sm"
              : "bg-gray-500 bg-opacity-75",
              "fixed inset-0 transition-opacity"
            )}
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={cn(
              minFullHeight === true ? "" : "items-center",
              "flex min-h-full justify-center p-4 text-center",
              containerClassName
            )}
          >
            <Transition.Child as={Fragment} {...childTransitionProps}>
              <Dialog.Panel
                className={cn(
                  widthMode === "normal" ? "sm:max-w-md"
                  : widthMode === "wide" ? "sm:max-w-4xl"
                  : widthMode === "ultraWide" ? "sm:max-w-7xl"
                  : "",
                  theme === "dark" ?
                    // The floating cards' own tone and shadow, borderless: every window and every card is one family, told apart from the blurred editor by a soft shadow rather than a hairline round it.
                    "rounded-xl bg-ttvCard shadow-floatingCard"
                  : bgColor === "primaryLight" ? "bg-primaryLight"
                  : "bg-white",
                  cn(
                    "relative w-full transform overflow-hidden px-4 pb-4 pt-5 text-left transition-all sm:my-8 sm:p-6",
                    theme !== "dark" && "rounded-lg shadow-xl",
                    panelClassName
                  )
                )}
              >
                <div className={contentClassName}>
                  <div className="flex items-center justify-between gap-3">
                    {
                      header === undefined ?
                        <Dialog.Title
                          as="h3"
                          className={cn(
                            // Dark modals live inside dense tool UIs (TTV): compact tool-scale title, not the marketing-page headline the light pages use.
                            theme === "dark" ?
                              "mb-3 text-base font-semibold text-white"
                            : "mb-4 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl",
                            centerTitle === true ? "mx-auto text-center" : ""
                          )}
                        >
                          {title}
                        </Dialog.Title>
                        // The name is still there for a screen reader; what is gone is the LINE of it, so the header can be the thing the dialog is for.
                      : <>
                          <Dialog.Title as="h3" className="sr-only">
                            {title}
                          </Dialog.Title>
                          <div className="mb-3 min-w-0 flex-1">{header}</div>
                        </>

                    }
                    {closeModal !== null && (
                      <button
                        type="button"
                        aria-label="Close dialog"
                        className={cn(
                          theme === "dark" ?
                            "mb-3 text-gray-400 transition-colors hover:text-white"
                          : "mb-4",
                          closeButtonClassName
                        )}
                        onClick={closeModal}
                      >
                        <XMarkIcon
                          className={cn(
                            theme === "dark" ? "h-5 w-5" : "h-6 w-6",
                            closeIconClassName
                          )}
                        />
                      </button>
                    )}
                  </div>
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
